import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArchive,
  FaCheck,
  FaChevronDown,
  FaChild,
  FaCog,
  FaCookieBite,
  FaDatabase,
  FaEnvelope,
  FaGlobe,
  FaKey,
  FaShareAlt,
  FaShieldAlt,
  FaSyncAlt,
  FaUser,
} from "react-icons/fa";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import PrivacyHeroVisual from "../../components/privacy/PrivacyHeroVisual";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import { fetchPublicCmsPage } from "../../services/cms.service";
import { buildPrivacyPageModel } from "../../utils/privacyPageCms";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/privacy_policy.scss";

const SECTION_ICONS = {
  user: FaUser,
  database: FaDatabase,
  cog: FaCog,
  share: FaShareAlt,
  cookie: FaCookieBite,
  archive: FaArchive,
  key: FaKey,
  shield: FaShieldAlt,
  globe: FaGlobe,
  child: FaChild,
  sync: FaSyncAlt,
  envelope: FaEnvelope,
};

function formatBadgeDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

function fallbackTodayLabel() {
  return new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getStickyHeaderOffset() {
  const header = document.querySelector(".hub-home-header");
  return (header?.getBoundingClientRect().height || 72) + 16;
}

function scrollElementIntoViewWithOffset(el) {
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset();
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}

export default function PrivacyPolicy() {
  const [page, setPage] = useState(null);
  const [openId, setOpenId] = useState("introduction");
  const [activeNavId, setActiveNavId] = useState("introduction");
  const pendingScrollIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPublicCmsPage("PRIVACY_POLICY");
        if (!cancelled) setPage(data);
      } catch {
        if (!cancelled) setPage(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const model = useMemo(() => buildPrivacyPageModel(page), [page]);
  const lastUpdated = formatBadgeDate(model.updatedAt) || fallbackTodayLabel();
  const effectiveDate = formatBadgeDate(model.effectiveDate) || lastUpdated;
  const heroBackgroundSrc = model.heroBackgroundUrl
    ? resolvePublicFileUrl(model.heroBackgroundUrl)
    : "";

  useEffect(() => {
    const firstId = model.sections[0]?.id || null;
    if (!firstId) {
      setOpenId(null);
      setActiveNavId(null);
      return;
    }
    setOpenId((prev) => (model.sections.some((s) => s.id === prev) ? prev : firstId));
    setActiveNavId((prev) => (model.sections.some((s) => s.id === prev) ? prev : firstId));
  }, [model.sections]);

  useEffect(() => {
    const id = pendingScrollIdRef.current;
    if (!id || openId !== id) return;
    pendingScrollIdRef.current = null;
    const run = () => {
      scrollElementIntoViewWithOffset(document.getElementById(`privacy-section-${id}`));
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, [openId]);

  const toggleSection = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
    setActiveNavId(id);
  };

  const scrollToSection = (id) => {
    setActiveNavId(id);
    setOpenId(id);
    pendingScrollIdRef.current = id;
    if (openId === id) {
      pendingScrollIdRef.current = null;
      scrollElementIntoViewWithOffset(document.getElementById(`privacy-section-${id}`));
    }
  };

  return (
    <div className="hub-privacy-page">
      <B2BHubHomeHeader />

      <main className="hub-privacy-page__main">
        <section
          className={`hub-privacy-hero${heroBackgroundSrc ? " hub-privacy-hero--has-bg" : ""}`}
          aria-labelledby="hub-privacy-title"
          style={
            heroBackgroundSrc
              ? { backgroundImage: `url(${heroBackgroundSrc})` }
              : undefined
          }
        >
          <div className="hub-privacy-hero__main">
            <div className="container-fluid hub-privacy-hero__inner">
              <div className="hub-privacy-hero__copy">
                <nav className="hub-privacy-breadcrumb" aria-label="Breadcrumb">
                  <Link to="/home">Home</Link>
                  <span aria-hidden>&gt;</span>
                  <span aria-current="page">Privacy Policy</span>
                </nav>

                <h1 id="hub-privacy-title" className="hub-privacy-hero__title">
                  Privacy Policy
                </h1>
                <p className="hub-privacy-hero__lead">{model.intro}</p>
              </div>

              {!heroBackgroundSrc ? <PrivacyHeroVisual /> : null}
            </div>
          </div>

          <div className="hub-privacy-hero__meta-bar">
            <div className="container-fluid hub-privacy-hero__meta-inner">
              <span className="hub-privacy-hero__meta-icon" aria-hidden>
                <FaShieldAlt />
                <FaCheck className="hub-privacy-hero__meta-check" />
              </span>
              <p className="hub-privacy-hero__meta-text">
                <strong>Last Updated:</strong> {lastUpdated}
                <span className="hub-privacy-hero__meta-sep" aria-hidden>
                  |
                </span>
                <strong>Effective Date:</strong> {effectiveDate}
              </p>
            </div>
          </div>
        </section>

        <section className="hub-privacy-body">
          <div className="container-fluid hub-privacy-body__inner">
            <aside className="hub-privacy-toc" aria-label="On this page">
              <h2 className="hub-privacy-toc__heading">On this page</h2>
              <ol className="hub-privacy-toc__list">
                {model.sections.map((section, index) => (
                  <li key={section.id}>
                    <button
                      type="button"
                      className={`hub-privacy-toc__link${
                        activeNavId === section.id ? " is-active" : ""
                      }`}
                      onClick={() => scrollToSection(section.id)}
                    >
                      <span className="hub-privacy-toc__num">{index + 1}.</span>
                      {section.title}
                    </button>
                  </li>
                ))}
              </ol>
            </aside>

            <div className="hub-privacy-accordion" role="list">
              {model.sections.map((section, index) => {
                const Icon = SECTION_ICONS[section.icon] || FaShieldAlt;
                const isOpen = openId === section.id;
                return (
                  <article
                    key={section.id}
                    id={`privacy-section-${section.id}`}
                    className={`hub-privacy-card${isOpen ? " is-open" : ""}`}
                    role="listitem"
                  >
                    <button
                      type="button"
                      className="hub-privacy-card__trigger"
                      aria-expanded={isOpen}
                      aria-controls={`privacy-panel-${section.id}`}
                      onClick={() => toggleSection(section.id)}
                    >
                      <span className="hub-privacy-card__num" aria-hidden>
                        {index + 1}
                      </span>
                      <span className="hub-privacy-card__copy">
                        <span className="hub-privacy-card__title">{section.title}</span>
                        <span className="hub-privacy-card__summary">{section.summary}</span>
                      </span>
                      <span className="hub-privacy-card__aside" aria-hidden>
                        <span className="hub-privacy-card__icon">
                          <Icon />
                        </span>
                        <FaChevronDown className="hub-privacy-card__chevron" />
                      </span>
                    </button>
                    <div
                      id={`privacy-panel-${section.id}`}
                      className="hub-privacy-card__panel"
                      hidden={!isOpen}
                    >
                      <div
                        className="hub-privacy-card__body cms-rendered-html"
                        dangerouslySetInnerHTML={{ __html: section.body }}
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="hub-privacy-cta" aria-label="Privacy support">
          <div className="container-fluid hub-privacy-cta__inner">
            <div className="hub-privacy-cta__banner">
              <span className="hub-privacy-cta__icon" aria-hidden>
                <FaShieldAlt />
              </span>
              <div className="hub-privacy-cta__text">
                <p className="hub-privacy-cta__title">{model.cta.title}</p>
                <p className="hub-privacy-cta__sub">{model.cta.subtitle}</p>
              </div>
              <Link to="/contact" className="hub-privacy-cta__btn">
                Contact Support
              </Link>
            </div>
          </div>
        </section>
      </main>

      <HubHomeFooter />
    </div>
  );
}
